/* global axios */
import ApiClient from './ApiClient';
import { resourcePath, requestConfig, withQuery } from './lais/httpHelpers';
import { PROPERTY_TRANSACTIONS_ENDPOINTS } from './lais/endpoints';

/**
 * PropertyTransactionsAPI — B13 closing-transaction / checklist client.
 *
 * Thin over the aggregate: index/show/timeline/due_items reads plus checklist
 * item commands (complete/reopen/waive/block/unblock/extend/add_evidence) and
 * installment planning. The controller delegates every write to the sole
 * writers and returns 422 (evidence/blocked), 403 (unauthorized waiver) or 409
 * (invalid transition) — surfaces render those explicitly. Gated by the closing
 * transactions feature (403 when disabled).
 */
class PropertyTransactionsAPI extends ApiClient {
  constructor() {
    super('property_transactions', { accountScoped: true });
  }

  get(params = {}) {
    return axios.get(withQuery(this.url, params), requestConfig());
  }

  show(id) {
    return axios.get(resourcePath(this.url, id), requestConfig());
  }

  async findByOffer(offerId) {
    const { data } = await this.get({ offer_id: offerId, per_page: 1 });
    const record = data?.data?.[0];
    return record?.id ? this.show(record.id) : { data: null };
  }

  timeline(id) {
    return axios.get(
      resourcePath(this.url, id, PROPERTY_TRANSACTIONS_ENDPOINTS.timeline),
      requestConfig()
    );
  }

  dueItems(id) {
    return axios.get(
      resourcePath(this.url, id, PROPERTY_TRANSACTIONS_ENDPOINTS.dueItems),
      requestConfig()
    );
  }

  create(payload = {}) {
    return axios.post(this.url, payload, requestConfig());
  }

  start(id, payload = {}) {
    return this.command(id, PROPERTY_TRANSACTIONS_ENDPOINTS.start, payload);
  }

  completeItem(id, itemId, evidence = {}) {
    return this.command(id, PROPERTY_TRANSACTIONS_ENDPOINTS.completeItem, {
      item_id: itemId,
      evidence,
    });
  }

  reopenItem(id, itemId, payload = {}) {
    return this.command(id, PROPERTY_TRANSACTIONS_ENDPOINTS.reopenItem, {
      item_id: itemId,
      ...payload,
    });
  }

  waiveItem(id, itemId, payload = {}) {
    return this.command(id, PROPERTY_TRANSACTIONS_ENDPOINTS.waiveItem, {
      item_id: itemId,
      ...payload,
    });
  }

  blockItem(id, itemId, payload = {}) {
    return this.command(id, PROPERTY_TRANSACTIONS_ENDPOINTS.blockItem, {
      item_id: itemId,
      ...payload,
    });
  }

  unblockItem(id, itemId) {
    return this.command(id, PROPERTY_TRANSACTIONS_ENDPOINTS.unblockItem, {
      item_id: itemId,
    });
  }

  addEvidence(id, itemId, refs = []) {
    return this.command(id, PROPERTY_TRANSACTIONS_ENDPOINTS.addEvidence, {
      item_id: itemId,
      refs,
    });
  }

  extendDeadline(id, itemId, newDeadline, payload = {}) {
    return this.command(id, PROPERTY_TRANSACTIONS_ENDPOINTS.extendDeadline, {
      item_id: itemId,
      new_deadline: newDeadline,
      ...payload,
    });
  }

  planInstallments(id, payload = {}) {
    return this.command(
      id,
      PROPERTY_TRANSACTIONS_ENDPOINTS.planInstallments,
      payload
    );
  }

  complete(id, payload = {}) {
    return this.command(id, PROPERTY_TRANSACTIONS_ENDPOINTS.complete, payload);
  }

  cancel(id, payload = {}) {
    return this.command(id, PROPERTY_TRANSACTIONS_ENDPOINTS.cancel, payload);
  }

  command(id, action, payload = {}) {
    return axios.post(
      resourcePath(this.url, id, action),
      payload,
      requestConfig()
    );
  }
}

export default new PropertyTransactionsAPI();
